import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Stack, tokens, type IconName } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

/**
 * DubaiDemoLanding — GVW-175, `/demo/dubai`.
 *
 * Cite: `design/30-engineering/ui-implementation/GVW-175/brief.md` and
 * `docs/strategy/PHASE2_CHARTER.md` §3.G.
 */
export function DubaiDemoLanding(): JSX.Element {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    document.title = 'Dubai vertical slice · GeoViewer';
    liveRegion.announce('status', 'Dubai vertical slice demo loaded.');
  }, [liveRegion]);

  const ctaLinks: Array<{
    to: string;
    icon: IconName;
    title: string;
    description: string;
  }> = [
    {
      to: '/workspace#mode=map',
      icon: 'map',
      title: '2D map view',
      description: 'Open the Dubai slice in the default map workspace.',
    },
    {
      to: '/workspace#mode=3d',
      icon: 'view_in_ar',
      title: '3D scene view',
      description: 'Inspect the same city context in the 3D workspace.',
    },
    {
      to: '/workspace#mode=compare',
      icon: 'visibility',
      title: 'Compare scenarios',
      description: 'Review side-by-side scenario analysis for the closure demo.',
    },
    {
      to: '/workspace/copilot',
      icon: 'smart_toy',
      title: 'Copilot rail',
      description: 'Open the analysis rail for guided geospatial questions.',
    },
    {
      to: '/workspace/share',
      icon: 'share',
      title: 'Share the view',
      description: 'Create a deterministic share link for the current state.',
    },
    {
      to: '/workspace/audit',
      icon: 'timeline',
      title: 'Audit trail',
      description: 'Inspect the evidence trail for the demo walkthrough.',
    },
  ];

  const performanceMetrics = [
    { label: 'First interactive', value: '< 2.5 s' },
    { label: 'Sustained canvas target', value: '60 FPS' },
    { label: 'Tile-load p95 target', value: '< 350 ms' },
    { label: 'Share-link budget', value: '< 1.8 KB' },
  ];

  return (
    <main
      role="main"
      aria-labelledby="gvw-175-heading"
      data-gvw-id="GVW-175"
      data-route="/demo/dubai"
      style={{
        minHeight: '100vh',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[4],
        boxSizing: 'border-box',
      }}
    >
      <Stack
        direction="column"
        gap={4}
        align="center"
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          marginInline: 'auto',
        }}
      >
        <header style={{ textAlign: 'center' }}>
          <h1
            id="gvw-175-heading"
            style={{
              margin: `0 0 ${tokens.space[3]}px 0`,
              fontSize: tokens.font.size['2xl'],
              lineHeight: tokens.font.lineHeight.tight,
              fontWeight: tokens.font.weight.semibold,
              color: tokens.color.ink.primary,
            }}
          >
            Dubai Vertical Slice Demo
          </h1>
          <p
            style={{
              maxWidth: tokens.layout.standaloneStateWide,
              margin: '0 auto',
              fontSize: tokens.font.size.md,
              lineHeight: tokens.font.lineHeight.normal,
              color: tokens.color.ink.secondary,
            }}
          >
            Experience the Phase 2 Dubai slice across 2D, 3D, compare, share,
            and audit workflows without leaving the GeoViewer workspace.
          </p>
        </header>

        <section aria-labelledby="gvw-175-performance-heading">
          <h2
            id="gvw-175-performance-heading"
            style={{
              margin: `0 0 ${tokens.space[3]}px 0`,
              fontSize: tokens.font.size.xl,
              fontWeight: tokens.font.weight.semibold,
              textAlign: 'center',
            }}
          >
            Performance Budget
          </h2>
          <div
            style={{
              display: 'flex',
              gap: tokens.space[3],
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {performanceMetrics.map((metric) => (
              <div
                key={metric.label}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: tokens.layout.popoverSm,
                  padding: tokens.space[3],
                  background: tokens.color.surface[1],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                }}
              >
                <span
                  style={{
                    marginBottom: tokens.space[1],
                    fontSize: tokens.font.size.sm,
                    color: tokens.color.ink.secondary,
                  }}
                >
                  {metric.label}
                </span>
                <strong
                  style={{
                    fontSize: tokens.font.size.md,
                    color: tokens.color.semantic.success,
                  }}
                >
                  {metric.value}
                </strong>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="gvw-175-try-heading" style={{ width: '100%' }}>
          <h2
            id="gvw-175-try-heading"
            style={{
              margin: `0 0 ${tokens.space[4]}px 0`,
              fontSize: tokens.font.size.xl,
              fontWeight: tokens.font.weight.semibold,
              textAlign: 'center',
            }}
          >
            Try the Slice
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.popoverSm}px, 1fr))`,
              gap: tokens.space[4],
            }}
          >
            {ctaLinks.map((cta) => (
              <Link
                key={cta.to}
                to={cta.to}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: tokens.space[3],
                  padding: tokens.space[4],
                  background: tokens.color.surface[1],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: `background ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}, border-color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: tokens.space[7],
                    height: tokens.space[7],
                    background: tokens.color.accent.primary,
                    borderRadius: tokens.radius.md,
                    color: tokens.color.surface[0],
                    flexShrink: 0,
                  }}
                >
                  <Icon name={cta.icon} size={24} decorative />
                </span>
                <span>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: tokens.space[1],
                      fontSize: tokens.font.size.md,
                      color: tokens.color.ink.primary,
                    }}
                  >
                    {cta.title}
                  </strong>
                  <span
                    style={{
                      display: 'block',
                      fontSize: tokens.font.size.sm,
                      lineHeight: tokens.font.lineHeight.normal,
                      color: tokens.color.ink.secondary,
                    }}
                  >
                    {cta.description}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="gvw-175-demo-target-heading"
          style={{
            width: '100%',
            maxWidth: tokens.layout.standaloneStateExtraWide,
            padding: tokens.space[4],
            background: tokens.color.surface[1],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          <Icon
            name="play_arrow"
            size={32}
            decorative
            style={{
              color: tokens.color.accent.primary,
              marginBottom: tokens.space[2],
            }}
          />
          <h2
            id="gvw-175-demo-target-heading"
            style={{
              margin: `0 0 ${tokens.space[2]}px 0`,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.semibold,
            }}
          >
            Phase 2 Demo Target
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: tokens.font.size.md,
              lineHeight: tokens.font.lineHeight.normal,
              color: tokens.color.ink.secondary,
            }}
          >
            This landing page anchors the closure demo while later work packages
            connect catalog data, profile mode, plugin actions, and multi-engine
            routing to the same Dubai narrative.
          </p>
        </section>
      </Stack>
    </main>
  );
}
